// ADM-1.3 · the words and decisions behind the commission switch on the Tenants
// page, kept out of the component so the load-bearing ones are tested.
//
// The write is `PUT /tenants/{id}/commission-costing`, which calls
// `set_tenant_commission_costing_enabled()` on the CALLER's token. The database
// is the authority; nothing here gates anything.

export interface CommissionState {
  label: 'On' | 'Off' | 'Unknown'
  /** The menu item to show, or null when the state is unknown. */
  action: string | null
}

/**
 * ⚠️ NULL IS UNKNOWN, NOT OFF. The list reports null for an account with no
 * `tenant_settings` row or when the read failed, and the RPC raises P0002 for
 * the first case. Offering "turn on" there would offer something that fails.
 */
export function commissionState(enabled: boolean | null | undefined): CommissionState {
  if (enabled === true) return { label: 'On', action: 'Turn commission off' }
  if (enabled === false) return { label: 'Off', action: 'Turn commission on' }
  return { label: 'Unknown', action: null }
}

export interface CommissionConfirmation {
  title: string
  description: string
  confirmLabel: string
  destructive: boolean
}

/**
 * Turning commission off only flips `tenant_settings.commission_costing_enabled`.
 * Verified 2026-10-02 off `pg_proc` / `pg_trigger`: the RPC is a single UPDATE
 * and the triggers on that table are guards, audit and governance capture. So
 * "nothing is deleted" is a fact, and it is the thing an operator needs to hear
 * before clicking.
 */
export function commissionConfirmation(tenantName: string, enabling: boolean): CommissionConfirmation {
  if (enabling) {
    return {
      title: `Turn on commission for ${tenantName}?`,
      description: `${tenantName} gets commission tracking and job costing: plans, earnings, pay periods, rate cards and job margin. This switch does not change billing.`,
      confirmLabel: 'Turn on',
      destructive: false,
    }
  }
  return {
    title: `Turn off commission for ${tenantName}?`,
    description: `Commission tracking and job costing disappear for everyone in ${tenantName}. Nothing is deleted: plans, earnings and cost lines stay, and come back if it is turned on again.`,
    confirmLabel: 'Turn off',
    destructive: true,
  }
}

/** Says the state the database RETURNED, which the endpoint echoes. */
export function commissionResultMessage(tenantName: string, enabled: boolean): string {
  return `Commission is ${enabled ? 'on' : 'off'} for ${tenantName}.`
}

/**
 * The endpoint maps every refusal to a sentence (403 names the token case, 404 a
 * missing settings row). Show that sentence rather than a generic failure.
 */
export function describeCommissionError(error: unknown, tenantName: string): string {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (error instanceof Error && error.message) return error.message
  return `Commission could not be changed for ${tenantName}.`
}
